import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptimizationJob } from '@/types/optimization';
import { OPTIMIZATION_SERVING_GATE_OR } from '@/lib/servingGate';

export interface ProductStats {
  total: number;
  optimized: number;
  notOptimized: number;
  pending: number;
  failed: number;
  /**
   * Bestand, den `enqueue-optimization-batches` auswaehlen WIRD: `not_optimized` oder
   * `failed` UND nicht vom Serving-Gate gesperrt (abgelehnt ohne heilbares Issue,
   * src/lib/servingGate.ts). Die Zahl auf dem Optimieren-Knopf kommt ab 26.08.2026 von
   * hier, nicht mehr aus `notOptimized + failed` -- sonst beschriftete der Knopf 44 und
   * der Lauf umfasste 0.
   */
  optimizable: number;
  uploaded: number;
  needsReupload: number;
  approved: number;
  /**
   * Freigegeben UND noch nicht bei Google: `approval_status='approved'` und
   * `upload_status <> 'uploaded'`.
   *
   * `approved` allein ist als Arbeitsvorrat unbrauchbar, weil die Spalte nach
   * einem erfolgreichen Upload NIE zurueckgesetzt wird (`process-upload-queue`
   * schreibt `approval_status` nicht; der einzige Ruecksetzer ist eine
   * Re-Optimierung, und die setzt auf `pending_review`). Ein Feed, der einmal
   * hochgeladen wurde, haette damit fuer immer `approved > 0` gemeldet.
   *
   * Zur Ueberschneidung mit `needsReupload`: dessen Produkte stehen durch die
   * Re-Optimierung auf `pending_review`, nicht auf `approved`. Das haelt
   * heute, ist aber eine Aussage ueber die SCHREIBER dieser Spalten und keine
   * Invariante der Tabelle — es gibt keinen Constraint, der die Kombination
   * `approved` + `needs_reupload` verboete.
   *
   * Beinahe entstanden waere sie in Task A5 (Werkbank macht freigegebene
   * Produkte wieder bearbeitbar; eine Handkorrektur an einer bereits
   * ausgelieferten Fassung sollte `needs_reupload` setzen). Gebaut ist das
   * NICHT: `upload_status` steht nicht im Spalten-GRANT fuer `authenticated`,
   * das `UPDATE` waere komplett an `42501` gescheitert. Kommt der Fall per
   * Migration oder RPC zurueck, entsteht die Kombination planmaessig.
   *
   * Die RECHNUNG hier traegt das ohnehin: `approved AND upload_status <>
   * 'uploaded'` faengt beide Faelle korrekt als Arbeitsvorrat.
   */
  approvedAwaitingUpload: number;
  pendingReview: number;
  /**
   * Wartet auf Freigabe UND ist noch nicht bei Google:
   * `approval_status='pending_review'` und `upload_status <> 'uploaded'`.
   *
   * `pendingReview` allein ist als "wirkt bei Google nicht"-Menge falsch,
   * sobald Auto-Upload aktiv ist: die Automatik laedt `pending_review`-Produkte
   * direkt zu GMC hoch (`_shared/upload-approval.ts`), und `process-upload-queue`
   * schreibt `approval_status` nie zurueck — die Produkte bleiben dauerhaft
   * `pending_review` + `upload_status='uploaded'` und sind laengst live.
   * Die Verlust-Zeile des Feed-Check-Kopfs haengt deshalb an DIESER Zahl,
   * waehrend `pendingReview` der Arbeitsvorrat der Werkbank bleibt.
   */
  pendingReviewAwaitingUpload: number;
  rejected: number;
}

/**
 * Die gemessenen Zahlen PLUS der Feed, ueber den sie gemessen wurden.
 *
 * Die Zuordnung liegt bewusst IM Ergebnis und nicht daneben: `placeholderData:
 * (prev) => prev` (unten) haelt bei einem Schluesselwechsel die Daten des
 * VORGAENGER-Feeds, und React Query meldet dafuer `status === 'success'`, also
 * `isPending === false`. Ein Ladeflag kann diesen Zustand deshalb gar nicht
 * beschreiben — gemessen gegen `@tanstack/react-query@5.83.0` steht direkt nach
 * dem Wechsel `isLoading === false` bei den Zahlen des alten Feeds.
 *
 * Wer aus den Zahlen eine HANDLUNG ableitet, muss also fragen "wozu gehoeren
 * sie", nicht "sind sie schon da". Der Anlass war die Feed-Check-Seite: die
 * Menge auf dem Optimieren-Knopf kommt aus `optimizable`, waehrend die
 * Server-Selection ueber `feed_id` waehlt — ein Klick im Platzhalter-Fenster
 * beschriftet 5.000 und reiht 20.000 ein. `user_optimization_usage` hat keinen
 * Decrement-Pfad.
 */
export interface ScopedProductStats extends ProductStats {
  feedId: string | null;
}

const EMPTY_STATS: ProductStats = {
  total: 0, optimized: 0, notOptimized: 0, pending: 0, failed: 0, optimizable: 0,
  uploaded: 0, needsReupload: 0, approved: 0, approvedAwaitingUpload: 0,
  pendingReview: 0, pendingReviewAwaitingUpload: 0, rejected: 0,
};

export function useProductStats(
  feedId: string | null,
  isOptimizing: boolean,
  activeJob: OptimizationJob | null | undefined,
  localJobInfo: { jobId: string; totalProducts: number } | null,
) {
  const { data: stats, refetch: refetchStats, isPending, isError, error } = useQuery({
    queryKey: ['simple-product-stats', feedId],
    queryFn: async (): Promise<ScopedProductStats> => {
      if (!feedId) return { ...EMPTY_STATS, feedId: null };

      const [totalRes, optimizedRes, pendingRes, failedRes, uploadedRes, needsReuploadRes, approvedRes, approvedAwaitingUploadRes, pendingReviewRes, pendingReviewAwaitingUploadRes, rejectedRes, optimizableRes] = await Promise.all([
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'pending'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'failed'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('upload_status', 'uploaded'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('upload_status', 'needs_reupload'),
        // K4-Befund: die fuenf Freigabe-Zaehler unten tragen den
        // `optimization_status='completed'`-Filter, den JEDE Werkbank-Flaeche
        // (Reiterzahlen, Liste, serverTotal) und der Upload-Server anlegen.
        // Ohne ihn zaehlten JourneyChips und Handlungskarte zehntausende
        // Phantom-Zeilen (pending_review + nie fertig optimiert), die keine
        // Ansicht je zeigt und kein Codepfad je aufloest.
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed').eq('approval_status', 'approved'),
        // `or` statt `neq`, weil `upload_status <> 'uploaded'` NULL-Zeilen
        // ausschliesst: ein Produkt ohne je gesetzten Upload-Status ist nicht
        // hochgeladen und gehoert genau deshalb in diese Zahl.
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed').eq('approval_status', 'approved').or('upload_status.is.null,upload_status.neq.uploaded'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed').eq('approval_status', 'pending_review'),
        // Gleiches `or`-Muster wie approvedAwaitingUpload: `neq` allein wuerde
        // NULL-Zeilen (nie hochgeladen) faelschlich ausschliessen.
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed').eq('approval_status', 'pending_review').or('upload_status.is.null,upload_status.neq.uploaded'),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).eq('optimization_status', 'completed').eq('approval_status', 'rejected'),
        // Serving-Gate (26.08.2026): dieselbe Menge, die der Server auswaehlt -- siehe Typ-Kommentar.
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('feed_id', feedId).in('optimization_status', ['not_optimized', 'failed']).or(OPTIMIZATION_SERVING_GATE_OR),
      ]);

      /**
       * V2-Befund: keine der zwoelf Zaehlabfragen prueft ihr `error`-Feld, und
       * `.count || 0` macht aus jedem Lesefehler eine gemessene 0 —
       * ununterscheidbar von "dieser Feed hat wirklich nichts".
       *
       * supabase-js lehnt einen PostgREST-Fehler nicht ab, sondern loest mit
       * `{count: null, error}` auf. Die Query galt damit als ERFOLGREICH, der
       * Fehlerzweig von React Query entstand gar nicht erst. Faellt allein die
       * `approvedAwaitingUpload`-Zaehlung aus, meldet der Hook 0 und die
       * Werkbank verliert ihren Upload-Knopf, obwohl serverseitig alles
       * bereitliegt.
       *
       * Werfen statt schoenrechnen: React Query behaelt dann die zuletzt
       * erfolgreich gemessenen Zahlen und markiert die Abfrage als fehlerhaft,
       * statt eine erfundene Null als Messung auszugeben. Gleiches Muster wie
       * `useWorkbenchStatusCounts` — dort steht die Begruendung ausgeschrieben:
       * "unbekannt" ist nicht "null Produkte".
       */
      const gescheitert = [
        totalRes, optimizedRes, pendingRes, failedRes, uploadedRes, needsReuploadRes,
        approvedRes, approvedAwaitingUploadRes, pendingReviewRes,
        pendingReviewAwaitingUploadRes, rejectedRes, optimizableRes,
      ].find((r) => r.error);
      if (gescheitert?.error) throw gescheitert.error;

      const total = totalRes.count || 0;
      const optimized = optimizedRes.count || 0;
      const pending = pendingRes.count || 0;
      const failed = failedRes.count || 0;
      const optimizable = optimizableRes.count || 0;
      const uploaded = uploadedRes.count || 0;
      const needsReupload = needsReuploadRes.count || 0;
      const approved = approvedRes.count || 0;
      const approvedAwaitingUpload = approvedAwaitingUploadRes.count || 0;
      const pendingReview = pendingReviewRes.count || 0;
      const pendingReviewAwaitingUpload = pendingReviewAwaitingUploadRes.count || 0;
      const rejected = rejectedRes.count || 0;

      return { feedId, total, optimized, notOptimized: total - optimized - pending - failed, pending, failed, optimizable, uploaded, needsReupload, approved, approvedAwaitingUpload, pendingReview, pendingReviewAwaitingUpload, rejected };
    },
    enabled: !!feedId,
    placeholderData: (prev) => prev,
    refetchInterval: isOptimizing ? 15000 : false,
  });

  const effectiveStats = React.useMemo((): ProductStats => {
    const base = stats || EMPTY_STATS;

    if (activeJob) {
      const jobCompleted = activeJob.completed_products || 0;
      const jobFailed = activeJob.failed_products || 0;
      const jobPending = activeJob.total_products - jobCompleted - jobFailed;
      return {
        ...base,
        pending: jobPending,
        optimized: base.optimized + jobCompleted,
        failed: base.failed + jobFailed,
        notOptimized: Math.max(0, base.total - (base.optimized + jobCompleted) - jobPending - (base.failed + jobFailed)),
        // Anders als notOptimized oben: der Enqueue hat diese Produkte schon VOR
        // dem Jobstart aus not_optimized/failed auf pending gesetzt, also misst
        // base.optimizable (gefiltert auf genau diese zwei Stati) sie gar nicht
        // mehr mit -- ein weiterer Abzug waere doppelt und druecke den Wert
        // faelschlich Richtung 0, waehrend andere Produkte noch optimierbar sind.
        optimizable: base.optimizable,
      };
    }

    if (localJobInfo) {
      return {
        ...base,
        pending: localJobInfo.totalProducts,
        notOptimized: Math.max(0, base.notOptimized - localJobInfo.totalProducts),
        optimizable: Math.max(0, base.optimizable - localJobInfo.totalProducts),
      };
    }

    return base;
  }, [stats, activeJob, localJobInfo]);

  /**
   * Die Zahlen sind noch nicht gemessen.
   *
   * Ohne dieses Signal ist `stats` nicht von "alles null" zu unterscheiden:
   * der Hook liefert vor der Aufloesung `EMPTY_STATS`. Wer daraus eine
   * Handlung ableitet (der Primaer-CTA auf /optimization-score tut das ueber
   * `deriveJourney`), bietet in diesem Fenster die falsche an — und ein Klick
   * darauf startet einen echten Lauf und verbraucht Kontingent.
   */
  const isLoading = !!feedId && isPending;

  /**
   * Ueber welchen Feed die zurueckgegebenen Zahlen gemessen wurden.
   *
   * Weicht der Wert von der aktuellen Auswahl ab, laeuft gerade das
   * Platzhalter-Fenster (siehe `ScopedProductStats`) — die Zahlen gehoeren dann
   * noch dem Vorgaenger, obwohl `isLoading` bereits false meldet. `null` heisst
   * "noch nie gemessen" und deckt sich mit einer leeren Auswahl.
   */
  const statsFeedId = stats?.feedId ?? null;

  return { stats: effectiveStats, refetchStats, isLoading, statsFeedId, isError, error };
}
